import { useEffect, useState } from 'react';
import { listAvailableSounds, getSoundUrl } from '../lib/sound-storage';
/**
 * Hook to fetch and manage sound library from Supabase Storage
 */
export function useSoundLibrary() {
    const [sounds, setSounds] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        const fetchSounds = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const soundPaths = await listAvailableSounds();
                // Convert file paths to sound items
                const soundItems = soundPaths.map((filePath) => {
                    // Extract name from file path
                    const fileName = filePath.split('/').pop() || '';
                    const nameWithoutExt = fileName.replace(/\.[^.]+$/, '');
                    // Determine collection
                    const collection = filePath.startsWith('og/') ? 'og' : 'default';
                    // Create ID from file path (replace spaces and special chars)
                    const id = filePath
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/-+/g, '-')
                        .replace(/^-|-$/g, '');
                    // Try to pick emoji based on filename
                    const emoji = getEmojiForSound(nameWithoutExt, collection);
                    return {
                        id,
                        name: formatSoundName(nameWithoutExt),
                        description: `Sound from ${collection} collection`,
                        emoji,
                        url: getSoundUrl(filePath),
                        collection,
                        filePath,
                    };
                });
                setSounds(soundItems);
            }
            catch (err) {
                console.error('Error fetching sound library:', err);
                setError(err instanceof Error ? err.message : 'Failed to fetch sounds');
            }
            finally {
                setIsLoading(false);
            }
        };
        fetchSounds();
    }, []);
    return { sounds, isLoading, error };
}
/**
 * Format sound name from file name
 */
function formatSoundName(fileName) {
    // Handle common patterns
    if (fileName.toLowerCase().includes('rain'))
        return 'Rain';
    if (fileName.toLowerCase().includes('guitar'))
        return 'Guitar';
    if (fileName.toLowerCase().includes('bossa'))
        return 'Bossa Nova Guitar';
    if (fileName.toLowerCase().includes('peaceful'))
        return 'Peaceful Guitar';
    // For Og Mandino scrolls/vows
    if (fileName.includes('Scroll')) {
        return fileName;
    }
    if (fileName.includes('Vow')) {
        return fileName;
    }
    // Generic formatting: capitalize words and replace hyphens/underscores with spaces
    return fileName
        .replace(/[-_]/g, ' ')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}
/**
 * Get emoji for sound based on its name
 */
function getEmojiForSound(fileName, collection) {
    const lower = fileName.toLowerCase();
    // Default collection emojis
    if (lower.includes('rain'))
        return '🌧️';
    if (lower.includes('bossa'))
        return '🎸';
    if (lower.includes('peaceful') || lower.includes('guitar'))
        return '🎸';
    // Og Mandino collection emojis
    if (collection === 'og') {
        if (lower.includes('scroll'))
            return '📜';
        if (lower.includes('vow'))
            return '✨';
        if (lower.includes('first'))
            return '💎';
        if (lower.includes('second'))
            return '🗺️';
        if (lower.includes('third'))
            return '✨';
        if (lower.includes('fourth'))
            return '🤝';
        if (lower.includes('fifth'))
            return '🌱';
        if (lower.includes('sixth'))
            return '🏆';
        if (lower.includes('seventh'))
            return '🔥';
        if (lower.includes('eighth'))
            return '⚡';
        if (lower.includes('ninth'))
            return '🌙';
        if (lower.includes('final') || lower.includes('prayer'))
            return '🙏';
        if (lower.includes('greet') || lower.includes('love'))
            return '❤️';
        if (lower.includes('persist') || lower.includes('succeed'))
            return '💪';
        if (lower.includes('day') || lower.includes('last'))
            return '⏳';
        if (lower.includes('act') || lower.includes('now'))
            return '🚀';
    }
    // Default emoji
    return '🎵';
}
